/**
 * TanStack Query hooks для Run History API (P1.6 — Audit Log + Run History).
 *
 * - useRunHistory(filters)        — list own pipeline runs (GET /run-history)
 * - useRunHistoryItem(id)         — single run (GET /run-history/{id})
 * - downloadRunOutput(id)         — download output_snapshot как .json файл
 *   (GET /run-history/{id}/output → Content-Disposition attachment)
 *
 * Фильтры прокидываются как query-параметры; undefined/null поля опускаются.
 * `page`/`page_size` — серверная пагинация (по умолчанию 1 / 50).
 *
 * Download реализован по образцу useDownloadDataset: blob → createObjectURL →
 * anchor click → revoke. Token не попадает в URL (auth через api interceptor).
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import api from '@/lib/api';

export interface RunHistoryItem {
  id: string;
  account_id: string;
  project_id: string | null;
  dataset_id: string | null;
  pipeline_type: string;
  status: string;
  is_test_run: boolean;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  estimated_credits: string | null;
  spent_credits: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  input_snapshot: Record<string, unknown> | null;
  output_snapshot: Record<string, unknown> | null;
  created_at: string;
}

export interface RunHistoryListResponse {
  items: RunHistoryItem[];
  total: number;
}

export interface RunHistoryFilters {
  project_id?: string | null;
  pipeline_type?: string | null;
  status?: string | null;
  from?: string | null;
  to?: string | null;
  search?: string | null;
  page?: number;
  page_size?: number;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

function buildParams(filters: RunHistoryFilters): Record<string, string> {
  const params: Record<string, string> = {};
  const page = filters.page ?? DEFAULT_PAGE;
  const pageSize = filters.page_size ?? DEFAULT_PAGE_SIZE;
  params.page = String(page);
  params.page_size = String(pageSize);

  if (filters.project_id) params.project_id = filters.project_id;
  if (filters.pipeline_type) params.pipeline_type = filters.pipeline_type;
  if (filters.status) params.status = filters.status;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.search) params.search = filters.search;
  return params;
}

export function useRunHistory(
  filters: RunHistoryFilters,
  options?: Partial<UseQueryOptions<RunHistoryListResponse>>,
) {
  return useQuery<RunHistoryListResponse>({
    queryKey: ['run-history', filters],
    queryFn: async () =>
      (
        await api.get<RunHistoryListResponse>('/run-history', {
          params: buildParams(filters),
        })
      ).data,
    ...options,
  });
}

export function useRunHistoryItem(
  id: string | null,
  options?: Partial<UseQueryOptions<RunHistoryItem>>,
) {
  return useQuery<RunHistoryItem>({
    queryKey: ['run-history', id],
    queryFn: async () =>
      (await api.get<RunHistoryItem>(`/run-history/${id}`)).data,
    enabled: Boolean(id),
    ...options,
  });
}

/**
 * Скачивает output_snapshot пайплайна как .json файл.
 * Бэкенд отдаёт Content-Disposition: attachment; body = JSON output_snapshot
 * (или literal `null` если run ещё не финализирован).
 *
 * Filename извлекается из Content-Disposition (RFC 6266 fallback),
 * иначе — `run-output-{id}.json`.
 */
export async function downloadRunOutput(id: string): Promise<void> {
  const r = await api.get<Blob>(`/run-history/${id}/output`, {
    responseType: 'blob',
    timeout: 60_000,
  });

  const contentDisposition = r.headers['content-disposition'] as string | undefined;
  let filename = `run-output-${id}.json`;
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i,
    );
    if (match) {
      const name = (match[1] || match[2] || '').trim();
      if (name) filename = name;
    }
  }

  const blob = r.data instanceof Blob ? r.data : new Blob([r.data as BlobPart]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke — Safari требует чтобы URL был жив в момент click.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}